import { useEffect, useState } from "react";
import WindowSearch from "@/components/WindowSearch";
import { toast } from "sonner";
import { Loader2, RefreshCw, Eye, CheckCircle2, AlertTriangle, Code2 } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function InstagramVersionControl() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmV, setConfirmV] = useState(null); // "v1" | "v2" | null

  const load = async () => {
    setLoading(true);
    try {
      const [v, s] = await Promise.all([api.igAdminGetVersion(), api.igAdminStats().catch(() => null)]);
      setData(v); setStats(s);
    } catch { toast.error("Could not load version settings."); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const activate = async (version) => {
    setSaving(true);
    try {
      const r = await api.igAdminSetVersion(version);
      setData((d) => ({ ...d, active_version: r.active_version }));
      toast.success(`Instagram Post Maker is now on ${version.toUpperCase()} for users`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to switch version"); }
    setSaving(false); setConfirmV(null);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#E1306C]" /></div>;

  const active = data?.active_version || "v1";
  const v2Enabled = !!data?.v2_enabled;
  const v3Enabled = !!data?.v3_enabled;
  const v4Enabled = !!data?.v4_enabled;
  const v5Enabled = !!data?.v5_enabled;
  const bv = stats?.by_version || {};

  const Card = ({ v, title, desc }) => {
    const disabled = (v === "v2" && !v2Enabled) || (v === "v3" && !v3Enabled) || (v === "v4" && !v4Enabled) || (v === "v5" && !v5Enabled);
    const pv = v.replace("v", "");
    return (
    <div data-testid={`igvc-card-${v}`} className={`flex-1 rounded-xl border p-4 ${active === v ? "border-[#00F0FF] bg-[#00F0FF]/[0.06]" : "border-white/10 bg-white/[0.02]"}`}>
      <div className="flex items-center justify-between">
        <div className="font-display text-lg font-black text-white">{title}</div>
        {active === v && <span className="flex items-center gap-1 rounded-full bg-[#00F0FF]/20 px-2 py-0.5 text-[11px] font-bold text-[#00F0FF]" data-testid={`igvc-active-${v}`}><CheckCircle2 size={12} /> Active</span>}
      </div>
      <p className="mt-1 text-xs text-[#A1A1AA]">{desc}</p>
      <div className="mt-3 flex gap-2">
        <button onClick={() => setConfirmV(v)} disabled={saving || active === v || disabled}
          data-testid={`igvc-activate-${v}`}
          className={`rounded-lg px-3 py-1.5 text-xs font-bold disabled:opacity-40 ${active === v ? "border border-white/15 text-[#A1A1AA]" : "bg-[#E1306C] text-white hover:bg-[#E1306C]/90"}`}>
          {active === v ? "Currently Active" : `Activate ${v.toUpperCase()}`}
        </button>
        <a href={`${BACKEND}/instagram-post-maker?v=${pv}`} target="_blank" rel="noreferrer"
          data-testid={`igvc-preview-${v}`}
          className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-white hover:border-[#8B5CF6]/60"><Eye size={13} /> Preview {v.toUpperCase()}</a>
        {v === "v5" && (
          <a href={`${BACKEND}/instagram-v5-workspace`} target="_blank" rel="noreferrer"
            data-testid="igvc-v5-workspace"
            className="flex items-center gap-1.5 rounded-lg border border-[#00F0FF]/40 px-3 py-1.5 text-xs font-semibold text-[#00F0FF] hover:bg-[#00F0FF]/10"><Code2 size={13} /> Code Workspace</a>
        )}
      </div>
    </div>
    );
  };

  return (
    <div data-testid="instagram-version-control">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-black text-white">Instagram Post Maker Version</h2>
          <p className="text-xs text-[#A1A1AA]">Choose which version normal users see. Switching is instant and never deletes data — V1 always stays available.</p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><RefreshCw size={13} /> Refresh</button>
      </div>

      {!v2Enabled && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-[#FF9F0A]/30 bg-[#FF9F0A]/[0.08] px-3 py-2 text-xs text-[#FFCF80]" data-testid="igvc-v2-disabled-note">
          <AlertTriangle size={14} /> V2 is turned OFF in Feature Control. Turn it ON there to let users access V2 (you can still Preview V2 as admin).
        </div>
      )}
      {!v3Enabled && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-[#FF9F0A]/30 bg-[#FF9F0A]/[0.08] px-3 py-2 text-xs text-[#FFCF80]" data-testid="igvc-v3-disabled-note">
          <AlertTriangle size={14} /> V3 is turned OFF in Feature Control. Turn it ON there to let users access V3 (you can still Preview V3 as admin).
        </div>
      )}
      {!v4Enabled && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-[#FF9F0A]/30 bg-[#FF9F0A]/[0.08] px-3 py-2 text-xs text-[#FFCF80]" data-testid="igvc-v4-disabled-note">
          <AlertTriangle size={14} /> V4 is turned OFF in Feature Control. Turn it ON (admin_only is fine) to activate/preview V4.
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <Card v="v1" title="Version 1" desc="The current stable Instagram Post Maker. Always available as a safe fallback." />
        <Card v="v2" title="Version 2" desc="Improved experience: paste anywhere, dynamic editable AI prompt, category-aware generation." />
        <Card v="v3" title="Version 3" desc="No templates — fully AI-generated from a structured brief, with OCR paste-to-text and an inline rewrite assistant." />
        <Card v="v4" title="Version 4" desc="Google image-input engine — sends your actual Main Subject Image to Google (BYOK) and keeps the real subject. No provider fallback." />
        <Card v="v5" title="Version 5" desc="Deterministic CODE compositor — keeps your uploaded subject pixel-for-pixel with layers/text/effects/export. NO AI, zero credits." />
      </div>

      {stats && (
        <div>
          <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-[#A1A1AA]">Generations by version</div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="igvc-stats">
            {[["V1 total", bv.v1 ?? 0], ["V2 total", bv.v2 ?? 0], ["V2 success", bv.v2_success ?? 0], ["V2 failed", bv.v2_failed ?? 0]].map(([l, val]) => (
              <div key={l} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                <div className="text-[11px] uppercase tracking-wide text-[#666]">{l}</div>
                <div className="mt-0.5 font-display text-xl font-black text-white">{val}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {confirmV && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="igvc-confirm" onClick={() => setConfirmV(null)}>
          <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#121212] p-6" onClick={(e) => e.stopPropagation()}>
            <WindowSearch />
            <div className="font-display text-lg font-black text-white">Switch Instagram Post Maker to {confirmV.toUpperCase()}?</div>
            <p className="mt-2 text-sm text-[#A1A1AA]">This changes the version shown to users. Your current {active.toUpperCase()} will remain available — nothing is deleted.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirmV(null)} className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-[#A1A1AA]">Cancel</button>
              <button onClick={() => activate(confirmV)} disabled={saving} data-testid="igvc-confirm-btn" className="flex items-center gap-1.5 rounded-lg bg-[#E1306C] px-4 py-2 text-sm font-bold text-white disabled:opacity-50">
                {saving ? <Loader2 size={14} className="animate-spin" /> : null} Activate {confirmV.toUpperCase()}
              </button>
            </div>
          </div>
        </div>
      )}
      <V4Panel />
    </div>
  );
}

function V4Panel() {
  const [cfg, setCfg] = useState(null);
  const [keyInput, setKeyInput] = useState("");
  const [model, setModel] = useState("");
  const [busy, setBusy] = useState("");
  const [testRes, setTestRes] = useState(null);
  const [cmp, setCmp] = useState(null);
  const [diag, setDiag] = useState(null);
  const [mainPath, setMainPath] = useState(null);
  const [mainUrl, setMainUrl] = useState(null);
  const [prompt, setPrompt] = useState("");

  const load = async () => {
    try {
      const c = await api.igV4Config(); setCfg(c); setModel(c.model);
      if (!prompt) setPrompt(c.default_prompt || "");
      setDiag(await api.igV4Diagnostics());
    } catch { /* admin only */ }
  };
  useEffect(() => { load(); }, []);
  if (!cfg) return null;

  const onUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy("upload");
    try { const r = await api.igUpload(f, "main"); setMainPath(r.path); setMainUrl(r.url); toast.success("Main Subject Image uploaded"); }
    catch { toast.error("Upload failed — use a PNG/JPG/WebP image"); }
    setBusy("");
  };
  const clearUpload = () => { setMainPath(null); setMainUrl(null); };

  const saveCfg = async () => {
    setBusy("save");
    try { await api.igV4SetConfig({ api_key: keyInput || undefined, model }); setKeyInput(""); toast.success("V4 Google config saved"); load(); }
    catch { toast.error("Save failed"); }
    setBusy("");
  };
  const testBody = () => ({ model, image_path: mainPath || undefined, prompt: (prompt || "").trim() || undefined });
  const runTest = async () => { setBusy("test"); setTestRes(null); try { setTestRes(await api.igV4Test(testBody())); load(); } catch (e) { toast.error("Test failed"); } setBusy(""); };
  const runCompare = async () => { setBusy("cmp"); setCmp(null); try { setCmp(await api.igV4Compare(testBody())); load(); } catch { toast.error("Compare failed"); } setBusy(""); };
  const rollback = async () => { try { const r = await api.igVersionRollback(); toast.success(`Rolled back to ${r.rolled_back_to.toUpperCase()}`); } catch { toast.error("Rollback failed"); } };

  const url = (u) => (u && u.startsWith("http") ? u : `${BACKEND}${u}`);
  const stageBadge = (ok) => <span className={ok ? "text-emerald-400" : "text-red-400"}>{ok ? "✓" : "✕"}</span>;

  return (
    <div className="mt-6 rounded-xl border border-[#00F0FF]/25 bg-[#0D0D0E] p-5" data-testid="ig-v4-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-base font-bold text-white">Version 4 — Google image-input test <span className="ml-2 rounded bg-[#00F0FF]/10 px-2 py-0.5 text-[11px] text-[#00F0FF]">ISOLATED</span></h3>
          <p className="mt-1 text-xs text-[#A1A1AA]">Sends the actual Main Subject Image to your own Google model as a real inline input. No fallback to any other provider.</p>
        </div>
        <button onClick={rollback} className="rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white/80 hover:bg-white/5" data-testid="ig-v4-rollback">Rollback active version</button>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <label className="mb-1 block text-[11px] text-white/50">Google API key {cfg.key_configured && <span className="text-emerald-400">(configured ✓)</span>}</label>
          <input type="password" className="w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} placeholder={cfg.key_configured ? "•••• stored — leave blank to keep" : "Paste GOOGLE_IMAGE_GENERATION_API_KEY"} data-testid="ig-v4-key" />
        </div>
        <div>
          <label className="mb-1 block text-[11px] text-white/50">Model</label>
          <select className="w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white" value={model} onChange={(e) => setModel(e.target.value)} data-testid="ig-v4-model">
            {cfg.model_options.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>
      <div className="mt-3 rounded-lg border border-white/10 bg-black/20 p-3">
        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-widest text-white/50">Main Subject Image (your own photo)</label>
        <div className="flex flex-wrap items-center gap-3">
          {mainUrl ? (
            <div className="flex items-center gap-3">
              <img src={url(mainUrl)} alt="main subject" className="h-20 w-20 rounded-lg border border-white/15 object-cover" data-testid="ig-v4-main-preview" />
              <button onClick={clearUpload} className="rounded-lg border border-white/15 px-2.5 py-1 text-[11px] text-white/70 hover:bg-white/5" data-testid="ig-v4-main-clear">Remove</button>
            </div>
          ) : null}
          <label className="cursor-pointer rounded-lg border border-[#00F0FF]/40 px-3 py-2 text-xs font-semibold text-[#00F0FF] hover:bg-[#00F0FF]/10" data-testid="ig-v4-upload-label">
            {busy === "upload" ? "Uploading…" : (mainUrl ? "Replace image" : "Upload your image")}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onUpload} data-testid="ig-v4-upload" />
          </label>
          <span className="text-[11px] text-white/40">{mainPath ? "Your image will be sent to Google as the real inline input." : "No image chosen — a built-in sample will be sent instead."}</span>
        </div>
      </div>

      <div className="mt-3">
        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-widest text-white/50">Prompt (include your headline here)</label>
        <textarea rows={5} className="w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 font-mono text-xs text-white" value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={cfg.default_prompt} data-testid="ig-v4-prompt" />
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={saveCfg} disabled={busy === "save"} className="rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/15" data-testid="ig-v4-save">{busy === "save" ? "Saving…" : "Save config"}</button>
        <button onClick={runTest} disabled={busy === "test"} className="rounded-lg bg-[#00F0FF] px-3 py-2 text-xs font-bold text-black" data-testid="ig-v4-test">{busy === "test" ? "Testing…" : (mainPath ? "Test with your image" : "Test Google Image Input")}</button>
        <button onClick={runCompare} disabled={busy === "cmp"} className="rounded-lg border border-[#00F0FF]/40 px-3 py-2 text-xs font-bold text-[#00F0FF]" data-testid="ig-v4-compare">{busy === "cmp" ? "Comparing…" : "Compare Text-only vs Image-input"}</button>
      </div>

      {testRes && (
        <div className="mt-4 rounded-lg border border-white/10 bg-black/30 p-4 text-xs" data-testid="ig-v4-test-result">
          {testRes.stages && (
            <div className="mb-3 grid grid-cols-2 gap-x-6 gap-y-1 text-white/70 sm:grid-cols-3">
              {Object.entries(testRes.stages).map(([k, v]) => <div key={k}>{stageBadge(v)} {k.replace(/_/g, " ")}</div>)}
            </div>
          )}
          {testRes.status === "success" ? (
            <div className="flex items-center gap-4">
              <img src={url(testRes.url)} alt="v4" className="h-40 rounded-lg border border-white/10" data-testid="ig-v4-test-image" />
              <div className="font-mono text-white/70">
                <div>Generated by: <span className="text-[#00F0FF]">Google</span></div>
                <div>Model: {testRes.diagnostics?.model}</div>
                <div>Image input: <span className="text-emerald-400">YES</span> ({testRes.diagnostics?.image_input_method})</div>
                <div>MIME: {testRes.diagnostics?.image_mime} · {testRes.diagnostics?.image_kb} KB</div>
                <div>HTTP: {testRes.diagnostics?.http} · {testRes.diagnostics?.response_ms} ms</div>
              </div>
            </div>
          ) : (
            <div className="text-red-300"><div className="font-bold">Google failed — no fallback</div><div>Stage: {testRes.stage} · HTTP: {testRes.http}</div><div>{testRes.message}</div>{testRes.provider_error && <div className="mt-1 text-white/40">Provider: {testRes.provider_error}</div>}</div>
          )}
        </div>
      )}

      {cmp && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="ig-v4-compare-result">
          {[["TEXT ONLY", cmp.text_only], ["IMAGE INPUT", cmp.image_input]].map(([label, r]) => (
            <div key={label} className="rounded-lg border border-white/10 bg-black/30 p-3 text-xs">
              <div className="mb-2 font-bold text-white">{label} <span className="text-white/40">· Google · {cmp.model}</span> {label === "IMAGE INPUT" && <span className="text-emerald-400">· Image Input: YES</span>}</div>
              {r.status === "success" ? <img src={url(r.url)} alt={label} className="h-44 w-full rounded object-contain" /> : <div className="text-red-300">Failed at {r.stage} (HTTP {r.http})</div>}
            </div>
          ))}
        </div>
      )}

      {diag?.tests?.length > 0 && (
        <div className="mt-4 text-xs text-white/50" data-testid="ig-v4-diagnostics">
          <div className="mb-1 font-bold uppercase tracking-widest text-white/40">Recent V4 tests · success {diag.success_rate ?? "—"}%</div>
          {diag.tests.slice(0, 6).map((t) => (
            <div key={t.id} className="flex gap-3 font-mono"><span className={t.status === "success" ? "text-emerald-400" : "text-red-400"}>{t.status}</span><span>{t.kind}</span><span>img:{String(t.image_input_present)}</span><span>{t.model}</span><span>{t.http}</span><span>{t.ms}ms</span></div>
          ))}
        </div>
      )}
    </div>
  );
}
